import {
  Card,
  InputNumber,
  Typography,
  Space,
  Row,
  Col,
  Button,
  message,
  Divider,
} from "antd";
import { useEffect, useState } from "react";
import { EmailListSettingCard } from "./EmailListComponent";
import axiosInstance from "../../services/axiosInstance";
import {
  GET_SETTINGS,
  UPDATE_NOTIFICATION_SETTINGS,
  UPDATE_SETTINGS,
} from "../../services/apiEndPoints";
import { INotificationSettings } from "../types/member.types";

const { Text, Title } = Typography;

interface NumberSettingCardProps {
  title: string;
  description?: string;
  value: number;
  min?: number;
  max?: number;
  step?: number;
  onChange: (value: number) => void;
  onSave: () => void;
  isDirty: boolean;
}

const NumberSettingCard: React.FC<NumberSettingCardProps> = ({
  title,
  description,
  value,
  min = 1,
  max = 100,
  step = 1,
  onChange,
  onSave,
  isDirty,
}) => {
  return (
    <Card>
      <Space direction="vertical" size={12} style={{ width: "100%" }}>
        {/* Title */}
        <Title level={5} style={{ margin: 0 }}>
          {title}
        </Title>

        {/* Description */}
        {description && <Text type="secondary">{description}</Text>}

        {/* Input */}
        <InputNumber
          value={value}
          min={min}
          max={max}
          step={step}
          style={{ width: "100%", height: 35 }}
          onChange={(val) => onChange(val ?? min)}
        />

        {/* Actions */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginTop: 8,
          }}
        >
          <Button type="primary" onClick={onSave} disabled={!isDirty}>
            Save
          </Button>
        </div>
      </Space>
    </Card>
  );
};

const SettingsPage: React.FC = () => {
  const [emails, setEmails] = useState<string[]>([]);
  const [savedEmails, setSavedEmails] = useState<string[]>([]);
  const [notificationSettings, setNotificationSettings] = useState<
    INotificationSettings[]
  >([]);

  const saveNotificationSettings = async (documentType: string) => {
    const setting = notificationSettings.find(
      (ns) => ns.documentType === documentType
    );
    if (!setting) return;
    const days = setting.notificationDays;
    // Save notification settings to API
    await axiosInstance.put(UPDATE_NOTIFICATION_SETTINGS, {
      documentType,
      notificationDays: days,
    });
    message.success("Notification settings saved successfully");
  };

  const saveEmails = async () => {
    setSavedEmails(emails);
    message.success("Settings saved successfully");
    await axiosInstance.put(UPDATE_SETTINGS, {
      emailIds: emails,
    });
  };

  const getSettingsData = async () => {
    // Fetch settings from API and set state
    const data = await axiosInstance.get(GET_SETTINGS);
    if (data && data.data) {
      const settings = data.data;
      setEmails(settings.emailIds || []);
      setSavedEmails(settings.emailIds || []);
      setNotificationSettings(settings.notificationSettings || []);
    }
  };

  useEffect(() => {
    getSettingsData();
  }, []);

  return (
    <Row gutter={[24, 24]}>
      <Col xs={24} md={24}>
        <EmailListSettingCard
          title="Notification Recipients"
          description="Add email addresses that should receive reminder notifications."
          value={emails}
          onChange={setEmails}
          onSave={saveEmails}
          isDirty={JSON.stringify(emails) !== JSON.stringify(savedEmails)}
        />
      </Col>

      <Divider />
      {notificationSettings.map((docType: INotificationSettings) => (
        <Col xs={24} md={8}>
          <NumberSettingCard
            title={`Reminder Days for ${docType.documentType}`}
            description="Set the number of days before an event to send reminder notifications to users."
            value={docType.notificationDays}
            min={1}
            max={180}
            onChange={(days) => {
              for (let i = 0; i < notificationSettings.length; i++) {
                if (
                  notificationSettings[i].documentType === docType.documentType
                ) {
                  const updatedSettings = [...notificationSettings];
                  updatedSettings[i] = {
                    ...updatedSettings[i],
                    notificationDays: days,
                  };
                  setNotificationSettings(updatedSettings);
                  break;
                }
              }
            }}
            onSave={() => {
              saveNotificationSettings(docType.documentType);
            }}
            isDirty={true}
          />
        </Col>
      ))}
    </Row>
  );
};

export default SettingsPage;
